/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import posthog from 'posthog-js'

/**
 * PostHog configuration options
 */
export interface PostHogConfig {
  apiKey: string
  apiHost: string
  enabled: boolean
}

/**
 * Get PostHog configuration from environment variables
 */
export function getPostHogConfig(): PostHogConfig {
  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY || ''
  const apiHost = import.meta.env.VITE_POSTHOG_HOST || 'https://us.posthog.com'

  // Enable in production by default, or when explicitly enabled
  // Disable in development unless explicitly enabled
  const explicitEnabled = import.meta.env.VITE_POSTHOG_ENABLED
  const enabled = explicitEnabled === 'true' ||
    (explicitEnabled !== 'false' && import.meta.env.PROD && !!apiKey)

  return { apiKey, apiHost, enabled }
}

/**
 * Initialize PostHog client
 * Returns true if initialization was successful
 */
export function initPostHog(): boolean {
  const config = getPostHogConfig()

  if (!config.enabled) {
    console.log('[PostHog] Analytics disabled (development mode or missing config)')
    return false
  }

  if (!config.apiKey) {
    console.warn('[PostHog] Missing API key - analytics disabled')
    return false
  }

  // Check if already initialized
  if (posthog.__loaded) {
    console.log('[PostHog] Already initialized, skipping')
    return true
  }

  try {
    posthog.init(config.apiKey, {
      api_host: config.apiHost,

      // Capture page views manually for better control with react-router
      capture_pageview: false,
      capture_pageleave: true,

      // Disable session recording per requirements
      disable_session_recording: true,

      // Enable autocapture for clicks and form submissions
      autocapture: true,

      // Respect Do Not Track browser setting
      // WARNING: If the browser has "Do Not Track" enabled (chrome://settings/cookies),
      // PostHog will automatically opt the user out and NO events will be tracked.
      // Set to false to track regardless of DNT setting.
      respect_dnt: true,

      // Persist user across sessions
      persistence: 'localStorage+cookie',

      // Use secure cookies in production
      secure_cookie: import.meta.env.PROD,

      // Bootstrap with empty distinct_id (will be set on identify)
      bootstrap: {
        distinctID: undefined,
      },

      // Callback when PostHog is ready
      loaded: (posthogInstance) => {
        // Expose to window for debugging
        if (typeof window !== 'undefined') {
          (window as any).posthog = posthogInstance
        }
      },
    })

    return true
  } catch (error) {
    console.error('[PostHog] Initialization failed:', error)
    return false
  }
}

/**
 * Get the PostHog client instance
 * Safe to call even if PostHog is not initialized
 */
export function getPostHog() {
  return posthog
}

/**
 * Check if PostHog is ready to track events
 */
export function isPostHogReady(): boolean {
  const config = getPostHogConfig()
  return config.enabled && posthog.__loaded
}
